import EditIcon from "@mui/icons-material/EditOutlined";
import ImportIcon from "@mui/icons-material/ExitToAppRounded";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { UserConsentDetails } from "../../../../api/types/userConsentTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import ImportConsentFileDialog from "./ImportConsentFileDialog";
import UserConsentContentEditor from "./UserConsentContentEditor";
import UserConsentDetailsPageActions from "./UserConsentDetailsPageActions";
import UserConsentDetailsPageTitle from "./UserConsentDetailsPageTitle";
import {
  cancelEditingContentAction,
  cancelImportFileAction,
  editHtmlContentAction,
  finishEditingContentAction,
  finishImportFileAction,
  getInitialUserConsentEditState,
  initFromConsentDetailsAction,
  startEditingContentAction,
  startImportFileAction,
  updateActivationDateAction,
  updateConfirmationTextAction,
  updateIsActiveAction,
} from "./userConsentEditState";
import UserConsentPreview from "./UserConsentPreview";

const UserConsentDetailsPage = () => {
  usePageTitle("User Consent");

  const { name } = useParams();
  const navigate = useNavigate();
  const { clientCode, hasPermissions } = useClientContext();
  const { sendNotification } = useNotificationContext();

  const [editState, setEditState] = useState(getInitialUserConsentEditState);

  const getUserConsentDetails = useCallback(() => adminApi.getUserConsentDetails(defined(name)), [name]);

  const [consentDetails, fetchConsentDetailsError, { setData: setConsentDetails }] = useFetch(getUserConsentDetails, {
    onSetData: (data) => setEditState(initFromConsentDetailsAction(data)),
  });

  const [portalSettingsResp, fetchPortalSettingsError] = useFetch(adminApi.getInvestorPortalSettings);

  const fetchError = fetchConsentDetailsError || fetchPortalSettingsError;
  if (fetchError) {
    logError(fetchError, "[UserConsentDetailsPage] fetchError");
    return <DataLoadingFailed />;
  }

  const loading = !consentDetails || !portalSettingsResp;
  if (loading) {
    return <InlineLoader />;
  }

  const hasEditPermissions = hasPermissions(["ManageInvestorPortalSettings"]);

  const userConsentsListPath = `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.investorPortalSettings}?tab=user_consents`;

  const handleDeleted = () => {
    navigate(userConsentsListPath);
  };

  const handleSaved = (updatedConsentDetails: UserConsentDetails) => {
    setEditState((prev) => ({ ...prev, touchedFields: [] }));
    sendNotification(`User consent updated successfully`);
    setConsentDetails(updatedConsentDetails);
  };

  const handleCancel = () => {
    setEditState(initFromConsentDetailsAction(consentDetails));
  };

  const handleEditContent = () => {
    setEditState(startEditingContentAction());
  };

  const handleImportFile = () => {
    setEditState(startImportFileAction());
  };

  const isEditingContentDisabled = editState.contentSource === "ImportedContent";

  return (
    <>
      <GeneralPageHeader
        TitleComponent={<UserConsentDetailsPageTitle consentDetails={consentDetails} />}
        showDefaultBackButtonTo={userConsentsListPath}
      >
        <UserConsentDetailsPageActions
          consentName={consentDetails.name}
          editState={editState}
          onSaved={handleSaved}
          onCancel={handleCancel}
          onDeleted={handleDeleted}
          actionsDisabled={loading}
        />
      </GeneralPageHeader>
      <Box sx={{ height: "100%", display: "flex" }}>
        <Stack spacing={3} sx={{ py: 2.5, px: 3, flex: "0 1 400px" }}>
          <Typography variant="subtitle1">Settings</Typography>

          <Paper variant="outlined">
            <Stack spacing={1} sx={{ p: 2 }}>
              <FormControlLabel
                sx={{ alignItems: "flex-start" }}
                control={
                  <Switch
                    disabled={!hasEditPermissions}
                    checked={editState.isActive}
                    onChange={(_, checked) => setEditState(updateIsActiveAction(checked))}
                  />
                }
                label={
                  <Stack sx={{ pl: 2 }}>
                    <Typography variant="subtitle2">Activation Date</Typography>
                    <Typography color="textSecondary" sx={{ mb: 1 }}>
                      Date when consent will be activated on the portal. If the date is not set, consent won’t be
                      visible to investors. After activation, you can deactivate the consent by turning off this toggle.
                      To activate again, turn the toggle back on and pick a new date.
                    </Typography>
                  </Stack>
                }
              />

              <DatePicker
                disablePast
                value={editState.activationDate}
                onChange={(date) => setEditState(updateActivationDateAction(date))}
                disabled={!hasEditPermissions || !editState.isActive}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { pl: 6 },
                    error: !!editState.validationResult.errors.activationDate,
                  },
                }}
              />
            </Stack>
          </Paper>

          <TextField
            disabled={!hasEditPermissions}
            fullWidth
            label="Confirmation text"
            value={editState.confirmationText}
            onChange={(e) => setEditState(updateConfirmationTextAction(e.target.value))}
            error={!!editState.validationResult.errors.confirmationText}
            helperText={
              editState.validationResult.errors.confirmationText ||
              "Customize the confirmation text displayed beside the checkbox users must select to agree with this consent"
            }
          />
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 2, px: 3 }}>
            {!editState.isEditingContent && (
              <>
                <Typography variant="subtitle1">Preview</Typography>
                {hasEditPermissions && (
                  <Stack direction="row" spacing={1}>
                    <Tooltip
                      arrow
                      title={
                        isEditingContentDisabled
                          ? "This consent was imported from a file. Editing in the content editor is no longer available."
                          : false
                      }
                    >
                      <span>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<EditIcon />}
                          disabled={isEditingContentDisabled}
                          onClick={handleEditContent}
                        >
                          Edit Content
                        </Button>
                      </span>
                    </Tooltip>
                    <Button variant="outlined" color="secondary" startIcon={<ImportIcon />} onClick={handleImportFile}>
                      Import File
                    </Button>
                  </Stack>
                )}
              </>
            )}

            {editState.isEditingContent && (
              <>
                <Typography variant="subtitle1">Content</Typography>
                <Stack direction="row" spacing={2}>
                  <Button variant="text" color="secondary" onClick={() => setEditState(cancelEditingContentAction())}>
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={() => setEditState(finishEditingContentAction())}>
                    Apply
                  </Button>
                </Stack>
              </>
            )}
          </Box>

          <Divider flexItem />

          <Box sx={{ bgcolor: "background.grey", py: 2.5, flex: 1, display: "flex", justifyContent: "center" }}>
            {!editState.isEditingContent && (
              <UserConsentPreview
                consentName={consentDetails.name}
                consentContent={editState.htmlContent}
                customAgreementText={editState.confirmationText}
                logoUrl={portalSettingsResp.settings.contactInvitationEmailCompanyLogoUrl}
              />
            )}

            {editState.isEditingContent && (
              <UserConsentContentEditor
                initialContent={editState.htmlContent}
                onChange={(content) => setEditState(editHtmlContentAction(content))}
              />
            )}
          </Box>
        </Box>
      </Box>
      {editState.isImportingFile && (
        <ImportConsentFileDialog
          onClose={() => setEditState(cancelImportFileAction())}
          onImport={(htmlContent: string) => setEditState(finishImportFileAction(htmlContent))}
        />
      )}
    </>
  );
};

export default UserConsentDetailsPage;
